/**
 * Pasted text as an HTML file, so it can go through /processDocument like any
 * other source.
 *
 * The parser refuses `text/plain` outright — it answers "Failed to find content
 * in parsed file" for a perfectly good .txt — but it reads HTML, and HTML is
 * the closest thing to plain text it accepts. Paragraphs come from blank lines,
 * which is how a teacher separates them when pasting.
 */
export const createHtmlFileFromText = (text: string) => {
  const paragraphs = text
    .split(/\n\s*\n/)
    .map(block => block.trim())
    .filter(Boolean)
    .map(block => `<p>${escapeHtml(block).replace(/\n/g, '<br />')}</p>`)
    .join('\n');
  const html = `<!doctype html><html><body>\n${paragraphs}\n</body></html>`;
  const timestamp = new Date()
    .toISOString()
    .slice(0, 19)
    .replace(/:/g, '-');
  return new File([new Blob([html], { type: 'text/html' })], `pasted-${timestamp}.html`, {
    type: 'text/html',
  });
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

export const createFileFromText = (text: string) => {
  const blob = new Blob([text], { type: 'text/plain' });
  const timestamp = new Date()
    .toISOString()
    .slice(0, 19)
    .replace(/:/g, '-');
  const fileName = `pasted-${timestamp}.txt`;
  const file = new File([blob], fileName, { type: 'text/plain' });
  return file;
};

/**
 * The length past which a paste stops being a prompt and starts being
 * material.
 *
 * Roughly a long paragraph. Below it a paste is someone quoting a sentence
 * into their instructions, which belongs in the composer; above it it is a
 * passage, an article or a worksheet, which belongs in the attachment strip
 * where the agent reads it as a document.
 */
export const PASTE_AS_ATTACHMENT_MIN_LENGTH = 300;

/**
 * Whether a pasted string should become a `.txt` attachment rather than being
 * typed into the composer.
 *
 * Measured after trimming, so trailing blank lines — which a copy out of a
 * document collects easily — don't push a short quote over the line.
 */
export function shouldAttachPastedText(text: string): boolean {
  return text.trim().length > PASTE_AS_ATTACHMENT_MIN_LENGTH;
}

/**
 * Lowercased extension with no leading dot ('pdf', 'docx'), or '' when the
 * name has none — a Google-native Doc/Slide picked from Drive, for instance.
 * Used to tag attachment metrics with what kind of file was added, since
 * `file.type` (the MIME type) is empty for formats browsers don't recognize
 * (.key is the common one) and isn't a stable dimension to filter on anyway.
 */
export function getFileExtension(fileName: string | undefined): string {
  if (!fileName) return '';
  const lastDot = fileName.lastIndexOf('.');
  return lastDot === -1 ? '' : fileName.slice(lastDot + 1).toLowerCase();
}

export type ImageNaturalSize = { naturalWidth: number; naturalHeight: number };

/**
 * The pixel dimensions of an image file, read in the browser before the bytes
 * are uploaded.
 *
 * The backend can only get these by downloading and decoding the file again,
 * so the client — which already holds the bytes — hands them over up front.
 *
 * Returns `undefined` for anything that isn't an image, and for an image the
 * browser can't decode (a HEIC out of an iPhone is the common one). Dimensions
 * are a nice-to-have, never a reason to fail an upload, so callers should treat
 * `undefined` as "not known" and carry on.
 */
export async function readImageNaturalSize(
  file: File,
): Promise<ImageNaturalSize | undefined> {
  if (!file.type.startsWith('image/')) return undefined;
  if (typeof window === 'undefined' || typeof window.Image === 'undefined') {
    return undefined;
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    return await new Promise<ImageNaturalSize | undefined>(resolve => {
      const image = new window.Image();
      image.onload = () =>
        resolve(
          image.naturalWidth && image.naturalHeight
            ? { naturalWidth: image.naturalWidth, naturalHeight: image.naturalHeight }
            : undefined,
        );
      image.onerror = () => resolve(undefined);
      image.src = objectUrl;
    });
  } catch {
    return undefined;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
